import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Check, Lock, Play, Star } from 'lucide-react';
import { useLab, studentBase, getCourseTemplateForChild } from '../LabContext';
import { ParentPreviewBadge } from '../LabUI';
import { computeDayStatus, getWeeksForCourse } from '../data/lessons';
import {
  getCourseMapDayPoint,
  getCourseMapGamePoint,
  getCourseMapPathPoints,
  getCourseMapPoint,
} from '../data/courseMapConfig';

const DAY_IDS = ['day-1', 'day-2', 'day-3', 'day-4', 'day-5'];

const valueText = (value, language) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value[language] || value.ru || value.en || '';
};

const compactTitle = (week, language) => {
  const title = valueText(week?.title, language);
  return title
    .replace(/^Неделя\s+\d+\s+—\s*/i, '')
    .replace(/^Week\s+\d+\s+—\s*/i, '');
};

const normalizedStatus = (status) => {
  if (status === 'complete') return 'completed';
  if (status === 'in-progress') return 'current';
  return status || 'locked';
};

const isDone = (status) => normalizedStatus(status) === 'completed';
const isLocked = (status) => normalizedStatus(status) === 'locked';

const statusLabel = (status, language) => {
  const s = normalizedStatus(status);
  if (s === 'completed') return language === 'ru' ? 'Завершено' : 'Completed';
  if (s === 'current' || s === 'open') return language === 'ru' ? 'Открыто' : 'Open';
  return language === 'ru' ? 'Закрыто' : 'Locked';
};

const weekStatus = (dayStatuses) => {
  if (dayStatuses.every((s) => isDone(s))) return 'completed';
  if (dayStatuses.every((s) => isLocked(s))) return 'locked';
  if (dayStatuses.some((s) => normalizedStatus(s) === 'current' || normalizedStatus(s) === 'open')) return 'current';
  return 'open';
};

const lessonRoute = (base, weekId, dayId) => `${base}/${weekId}/${dayId}`;

const CourseMap = ({ childId: explicitChildId, className = '' }) => {
  const { language, user, childProgress } = useLab();
  const params = useParams();
  const childId = explicitChildId || params.studentId || user?.studentId || 'max';
  const base = studentBase(childId);
  const progress = childProgress(childId);
  const isParent = user?.role === 'parent' || user?.role === 'super_admin';
  const courseTemplate = getCourseTemplateForChild(user, childId);
  const courseWeeks = getWeeksForCourse(courseTemplate);

  const mapWeeks = useMemo(() => {
    return courseWeeks
      .slice(0, 10)
      .map((week) => {
        const hasRealDays = Array.isArray(week.days) && week.days.length > 0;
        // Placeholder weeks (Anita 2–10) have no day data and must remain
        // locked + non-clickable for every role. We still render their
        // island marker so the map shape stays whole.
        if (!hasRealDays || week.locked || week.placeholder) {
          return {
            week,
            point: getCourseMapPoint(week.id),
            title: language === 'ru' ? 'Скоро' : 'Coming soon',
            status: 'locked',
            dayStatuses: ['locked', 'locked', 'locked', 'locked', 'locked'],
            openDayId: 'day-1',
            isPlaceholder: true,
          };
        }
        const dayStatuses = DAY_IDS.map((dayId) => normalizedStatus(computeDayStatus(progress, week.id, dayId, courseWeeks)));
        const status = weekStatus(dayStatuses);
        const firstOpenIndex = dayStatuses.findIndex((s) => !isDone(s) && !isLocked(s));
        const firstIncompleteIndex = dayStatuses.findIndex((s) => !isDone(s));
        const openDayIndex = isParent ? Math.max(0, firstIncompleteIndex) : Math.max(0, firstOpenIndex);
        return {
          week,
          point: getCourseMapPoint(week.id),
          title: compactTitle(week, language),
          status,
          dayStatuses,
          openDayId: DAY_IDS[openDayIndex] || 'day-1',
          isPlaceholder: false,
        };
      });
  }, [language, progress, isParent, courseWeeks]);

  const currentMarker = useMemo(() => {
    for (const item of mapWeeks) {
      if (item.isPlaceholder) continue;
      for (let idx = 0; idx < DAY_IDS.length; idx += 1) {
        const status = item.dayStatuses[idx];
        if (!isDone(status) && !isLocked(status)) {
          return getCourseMapDayPoint(item.week.id, idx + 1);
        }
      }
    }
    const completedWeeks = mapWeeks.filter((item) => !item.isPlaceholder && item.status === 'completed');
    if (completedWeeks.length) return completedWeeks[completedWeeks.length - 1].point;
    return getCourseMapPoint('week-1');
  }, [mapWeeks]);

  const gameUnlocked = isParent || normalizedStatus(computeDayStatus(progress, 'week-10', 'day-5', courseWeeks)) === 'completed';
  const gamePoint = getCourseMapGamePoint();
  const pathPoints = getCourseMapPathPoints();

  return (
    <section className={`course-map-shell course-map-shell-v2 ${className}`} data-testid="course-map">
      <div className="course-map-heading">
        <div>
          <p className="course-map-kicker">{language === 'ru' ? 'Большая карта курса' : 'Full course map'}</p>
          <h2>{language === 'ru' ? 'Карта курса Нимни' : 'Nimny Course Map'}</h2>
          <p>{language === 'ru' ? 'Нажми на неделю или день, чтобы открыть занятие.' : 'Tap a week or day to open the lesson.'}</p>
        </div>
        {isParent && <ParentPreviewBadge />}
      </div>

      <div className="course-map-board course-map-board-v2">
        <img
          src="/assets/course-map/course-map-bg-v2.webp"
          alt=""
          className="course-map-bg"
          draggable={false}
        />

        <svg className="course-map-route" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          <polyline
            points={pathPoints.map((p) => `${p.x},${p.y}`).join(' ')}
            className="course-map-route-line course-map-route-line--shadow"
          />
          <polyline
            points={pathPoints.map((p) => `${p.x},${p.y}`).join(' ')}
            className="course-map-route-line"
          />
        </svg>

        {mapWeeks.map((item, weekIndex) => {
          const weekNum = weekIndex + 1;
          // Placeholders (Anita weeks 2–10) are never clickable, even for
          // a parent — there are no lessons behind them to preview.
          const weekCanOpen = !item.isPlaceholder && (isParent || item.status !== 'locked');
          const to = lessonRoute(base, item.week.id, item.openDayId);
          const completedCount = item.dayStatuses.filter(isDone).length;

          const nodeContent = (
            <>
              <span className="course-map-week-number">{weekNum}</span>
              <span className="course-map-week-state">
                {item.status === 'completed' ? <Check size={14} /> : item.status === 'locked' ? <Lock size={13} /> : <Star size={14} />}
              </span>
              <span className="course-map-week-label">
                <strong>{language === 'ru' ? `Неделя ${weekNum}` : `Week ${weekNum}`}</strong>
                <small>{item.title}</small>
                {!item.isPlaceholder && <em>{completedCount}/5</em>}
              </span>
            </>
          );

          const commonProps = {
            className: `course-map-week-node course-map-status-${item.status}`,
            style: { left: `${item.point.x}%`, top: `${item.point.y}%` },
            'data-testid': `course-map-week-${weekNum}`,
            title: `${language === 'ru' ? `Неделя ${weekNum}` : `Week ${weekNum}`}: ${item.title}`,
          };

          return (
            <React.Fragment key={item.week.id}>
              {weekCanOpen ? (
                <Link to={to} {...commonProps}>{nodeContent}</Link>
              ) : (
                <div {...commonProps}>{nodeContent}</div>
              )}

              {/* Day dots only for weeks with real lesson data. Placeholder
                  weeks (Anita 2–10) intentionally have no day dots. */}
              {!item.isPlaceholder && DAY_IDS.map((dayId, dayIdx) => {
                const point = getCourseMapDayPoint(item.week.id, dayIdx + 1);
                const status = item.dayStatuses[dayIdx];
                const dayCanOpen = isParent || !isLocked(status);
                const className = `course-map-day-dot course-map-status-${status}`;
                const label = `${language === 'ru' ? `Неделя ${weekNum}, день ${dayIdx + 1}` : `Week ${weekNum}, Day ${dayIdx + 1}`} — ${statusLabel(status, language)}`;
                const dot = <span>{dayIdx + 1}</span>;

                return dayCanOpen ? (
                  <Link
                    key={`${item.week.id}-${dayId}`}
                    to={lessonRoute(base, item.week.id, dayId)}
                    className={className}
                    style={{ left: `${point.x}%`, top: `${point.y}%` }}
                    title={label}
                    data-testid={`course-map-${item.week.id}-${dayId}`}
                  >
                    {dot}
                  </Link>
                ) : (
                  <div
                    key={`${item.week.id}-${dayId}`}
                    className={className}
                    style={{ left: `${point.x}%`, top: `${point.y}%` }}
                    title={label}
                    data-testid={`course-map-${item.week.id}-${dayId}`}
                  >
                    {dot}
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}

        <Link
          to={`${base}/nimny-night-run`}
          className={`course-map-game-node ${gameUnlocked ? 'course-map-status-current' : 'course-map-status-locked'}`}
          style={{ left: `${gamePoint.x}%`, top: `${gamePoint.y}%` }}
          data-testid="course-map-night-runner"
        >
          {gameUnlocked ? <Play size={22} /> : <Lock size={20} />}
          <strong>{language === 'ru' ? 'Ночная игра' : 'Night Game'}</strong>
        </Link>

        <div
          className="course-map-nimny-marker"
          style={{ left: `${currentMarker.x}%`, top: `${currentMarker.y}%` }}
          data-testid="course-map-nimny-marker"
          aria-hidden="true"
        >
          <span className="course-map-lantern-glow" />
          <img src="/assets/course-map/nimny-map-stand.webp" alt="" />
        </div>

        <div className="course-map-twinkle course-map-twinkle-1" />
        <div className="course-map-twinkle course-map-twinkle-2" />
        <div className="course-map-twinkle course-map-twinkle-3" />
      </div>
    </section>
  );
};

export default CourseMap;
